// Adapter layer over the multi-tenant FastAPI backend.
//
// Phase 2 reality check:
//   - Patients live at /api/patients with an enriched shape (birth_date,
//     assigned_room_id, emergency_contacts[]). Legacy /residents endpoints
//     are no longer used by this adapter.
//   - Rooms / Corpus / Organizations are real backend entities.
//   - OTA + firmware history do not exist yet — still synthesized below.
//
// This file is the SINGLE place that hides field-name drift from the rest
// of the app. When the backend renames a field, only this file changes —
// pages keep consuming the same normalised shape.

import { api } from './api';
// Device type is passed through raw (concrete model); the catalog resolves labels/icons.

// ---------- Patients ----------
// Backend exposes:
//   GET /api/patients               — paginated, scoped by X-Org-Id
//   GET /api/patients/:id           — single patient (rich shape)
//   GET /api/patients/:id/devices   — devices assigned to the patient
//   GET /api/patients/:id/events    — events for the patient
// Server returns enriched fields: birth_date, assigned_room_id (UUID),
// emergency_contacts (array), photo (alias for avatar_url), etc.
// Legacy aliases (avatar_url, notes, emergency_contact_name/phone) are kept
// by the backend for compatibility — we surface both shapes.

export async function listPatients(params = {}) {
  const { limit = 200, offset = 0, ...rest } = params;
  const clean = Object.fromEntries(
    Object.entries({ limit, offset, ...rest }).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );
  const { data, headers } = await api.get('/patients', { params: clean });
  const list = Array.isArray(data) ? data : [];
  return {
    items: list.map(normalisePatient),
    total: parseInt(headers?.['x-total-count'] ?? list.length, 10),
    limit: parseInt(headers?.['x-limit'] ?? limit, 10),
    offset: parseInt(headers?.['x-offset'] ?? offset, 10),
  };
}

export async function getPatient(id) {
  const { data } = await api.get(`/patients/${id}`);
  return data ? normalisePatient(data) : null;
}

function normalisePatient(p) {
  // New API:
  //   assigned_room_id (UUID) — what /rooms/:id expects
  //   room (string)           — human label
  // Legacy fallback: `room` was the display string only (no UUID).
  const roomId = p.assigned_room_id || p.room_id || p.room || null;
  const roomLabel = p.room || p.room_label || (roomId ? String(roomId) : '—');
  // Emergency contacts: prefer the new array; otherwise synthesize from legacy fields.
  const ec = Array.isArray(p.emergency_contacts) && p.emergency_contacts.length > 0
    ? p.emergency_contacts
    : (p.emergency_contact_name || p.emergency_contact_phone)
      ? [{
          name: p.emergency_contact_name || '',
          phone: p.emergency_contact_phone || '',
          relationship: p.emergency_contact_relationship || '',
        }]
      : [];
  return {
    ...p,
    room_id: roomId,
    room_label: roomLabel,
    avatar_url: p.avatar_url || p.photo || null,
    notes: p.notes || p.caregiver_notes || '',
    emergency_contacts: ec,
    emergency_contact_name: p.emergency_contact_name || ec[0]?.name || '',
    emergency_contact_phone: p.emergency_contact_phone || ec[0]?.phone || '',
  };
}

// ---------- Devices ----------

export async function listDevices(params = {}) {
  const { data, headers } = await api.get('/devices', { params: { limit: 200, ...params } });
  const list = Array.isArray(data) ? data : [];
  return {
    items: list.map(normaliseDevice),
    total: parseInt(headers?.['x-total-count'] ?? list.length, 10),
  };
}

export async function getDevice(id, fallbackList) {
  // Backend has no /devices/:id yet — synthesize from list.
  if (Array.isArray(fallbackList)) {
    const d = fallbackList.find((x) => x.id === id);
    if (d) return enrichDeviceDetail(normaliseDevice(d));
  }
  const { items } = await listDevices();
  const d = items.find((x) => x.id === id);
  return d ? enrichDeviceDetail(d) : null;
}

function normaliseDevice(d) {
  // Backend field naming has drifted across endpoints — accept any of these.
  const rawType = d.type || d.device_type || d.kind || d.model_type || null;
  const type = rawType;
  // Synthesize a deterministic signal score from MAC so the UI feels alive
  // without lying about real telemetry. Replace once backend exposes it.
  const mac = d.mac || d.mac_address || '';
  const seed = mac.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const signal = d.signal_quality ?? ((seed % 70) + 30);
  return {
    ...d,
    type,
    raw_type: rawType,
    mac_address: d.mac || d.mac_address || null,
    signal_quality: signal,
    connectivity_status: d.online ? 'online' : 'offline',
    firmware_version: d.firmware || d.firmware_version || 'v1.0.0',
    ota_enabled: d.ota_enabled ?? true,
    ota_status: d.ota_status || 'idle', // 'idle' | 'pending' | 'deploying' | 'failed'
  };
}

function enrichDeviceDetail(d) {
  // Mocked firmware history + OTA config until backend ships it.
  return {
    ...d,
    firmware_history: [
      { version: d.firmware_version, deployed_at: d.last_seen, status: 'active' },
      { version: 'v0.9.4', deployed_at: '2025-11-12T09:30:00Z', status: 'rolled_back' },
      { version: 'v0.9.2', deployed_at: '2025-09-04T12:00:00Z', status: 'archived' },
    ],
    ota_config: {
      fall_sensitivity: d.type === 'wristband' ? 'medium' : null,
      transmission_interval_s: 30,
      low_battery_threshold: 20,
    },
  };
}

// ---------- Rooms (real backend endpoints) ----------
// Backend now exposes:
//   GET /api/rooms                       — paginated list, scoped by X-Org-Id
//   GET /api/rooms/:id                   — room detail
//   GET /api/rooms/:id/patients          — patients in the room
//   GET /api/rooms/:id/devices           — devices assigned to the room
//   GET /api/rooms/:id/events            — events in the room (heartbeats excluded)
// Each room object carries pre-computed counts and door/window state, so
// the UI no longer derives anything client-side.

export async function listRooms(params = {}) {
  const { limit = 50, offset = 0, ...rest } = params;
  // Strip undefined/empty filters so we don't pollute the query string.
  const clean = Object.fromEntries(
    Object.entries({ limit, offset, ...rest }).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );
  const res = await api.get('/rooms', { params: clean });
  const list = Array.isArray(res.data) ? res.data : [];
  return {
    items: list,
    total: parseInt(res.headers?.['x-total-count'] ?? list.length, 10),
    limit: parseInt(res.headers?.['x-limit'] ?? limit, 10),
    offset: parseInt(res.headers?.['x-offset'] ?? offset, 10),
  };
}

export async function getRoom(id) {
  const { data } = await api.get(`/rooms/${id}`);
  return data || null;
}

// Buildings (a.k.a. corpus). Read list for the active org.
export async function listBuildings() {
  const { data } = await api.get('/corpus');
  return Array.isArray(data) ? data : [];
}

export async function getRoomPatients(id) {
  const { data } = await api.get(`/rooms/${id}/patients`);
  return Array.isArray(data) ? data.map(normalisePatient) : [];
}

export async function getRoomDevices(id) {
  const { data } = await api.get(`/rooms/${id}/devices`);
  const list = Array.isArray(data) ? data : [];
  return list.map(normaliseDevice);
}

export async function getRoomEvents(id, params = {}) {
  const { data } = await api.get(`/rooms/${id}/events`, { params: { limit: 30, ...params } });
  return Array.isArray(data) ? data : [];
}

// H4 Pro temperature/humidity history — up to 48 hourly-averaged buckets for
// the past 48h. Sparse (missing hours produce no entry). Returns [] on 404/empty.
export async function getRoomSensorReadings(id) {
  const { data } = await api.get(`/rooms/${id}/sensor-readings`);
  return Array.isArray(data) ? data : [];
}

// ---------- Alerts ----------

export async function listAlerts(params = {}) {
  const { data, headers } = await api.get('/alerts', { params: { limit: 50, ...params } });
  const list = Array.isArray(data) ? data : [];
  return {
    items: list.map(normaliseAlert),
    total: parseInt(headers?.['x-total-count'] ?? list.length, 10),
  };
}

function normaliseAlert(a) {
  // Backend currently has only ack/unack — synthesize a 3-state status
  // (new | acknowledged | resolved). "Resolved" is mocked client-side
  // for now (kept in localStorage so the UI survives reloads in Phase 1).
  const resolvedLocally = readLocalResolved().has(a.id);
  let status = 'new';
  if (resolvedLocally) status = 'resolved';
  else if (a.acknowledged) status = 'acknowledged';
  return {
    ...a,
    status,
    notes: a.notes || readLocalNotes(a.id),
  };
}

const LS_RESOLVED = 'e2care.alerts.resolved';
const LS_NOTES = 'e2care.alerts.notes';
function readLocalResolved() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_RESOLVED) || '[]')); }
  catch { return new Set(); }
}
function readLocalNotes(id) {
  try {
    const m = JSON.parse(localStorage.getItem(LS_NOTES) || '{}');
    return m[id] || [];
  } catch { return []; }
}
export function resolveAlertLocal(id) {
  const s = readLocalResolved();
  s.add(id);
  localStorage.setItem(LS_RESOLVED, JSON.stringify(Array.from(s)));
}
export function addAlertNoteLocal(id, note) {
  let m = {};
  try { m = JSON.parse(localStorage.getItem(LS_NOTES) || '{}'); } catch {}
  if (!m[id]) m[id] = [];
  m[id].push({ note, at: new Date().toISOString() });
  localStorage.setItem(LS_NOTES, JSON.stringify(m));
}

export async function acknowledgeAlert(id) {
  await api.post(`/alerts/${id}/ack`);
}

// ---------- Events ----------

export async function listEvents(params = {}) {
  const { data, headers } = await api.get('/events', { params: { limit: 200, ...params } });
  const list = Array.isArray(data) ? data : [];
  return {
    items: list,
    total: parseInt(headers?.['x-total-count'] ?? list.length, 10),
  };
}
